import './SparePartsCollectionList.scss';

import { List, useTable } from '@refinedev/antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AppTable from '@/components/general/AppTable';
import LinkTextField from '@/components/general/LinkTextField';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import { COMPANY_URLS } from '@/constants/urls';
import { useUserContext } from '@/contexts/UserContext';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { RESOURCES } from '@/Resources';

import SparePartsFilter from './SparePartsFilter/SparePartsFilter';

const SparePartsCollectionList = () => {
  const { t } = useTranslation();
  const { pageSizePreference } = useUserContext();
  const [isReady, setIsReady] = useState(false);
  const {
    tableProps,
    filters,
    setFilters,
    currentPage,
    setCurrentPage: setCurrent,
    pageSize,
    setPageSize,
    sorters,
    setSorters,
  } = useTable<Shipment>({
    resource: RESOURCES.SHIPMENTS,
    meta: { contains_sale_items: true },
    pagination: {
      pageSize: pageSizePreference,
    },
    queryOptions: {
      refetchInterval: 300000, // 5 minutes
      retry: 3,
      enabled: isReady,
    },
  });

  useListStatePersistence({
    resource: RESOURCES.CASE_RESOLUTION,
    filters,
    setFilters,
    sorters,
    setSorters,
    currentPage,
    setCurrentPage: setCurrent,
    pageSize,
    setPageSize,
    onReady: () => setIsReady(true),
  });

  return (
    <List
      headerButtons={() => (
        <SparePartsFilter filters={filters} setFilters={setFilters} setCurrent={setCurrent} />
      )}>
      <AppTable
        {...tableProps}
        rowKey={'id'}
        scroll={{ x: 700 }}
        className="SparePartsCollectionList">
        <AppTable.Column
          title={t('Envío')}
          render={(_, record) => (
            <LinkTextField
              label={`Envío #${record.id}`}
              to={`/${RESOURCES.SPARE_PARTS_COLLECTIONS}/${record.id}`}
              local
              withoutColor
            />
          )}
        />
        <AppTable.Column
          title={t('Destino')}
          width={240}
          render={(_, record) => (
            <LinkTextField
              label={
                record.company?.full_name ? `${record.company.full_name}` : `${record.company.id}`
              }
              to={`${COMPANY_URLS.ADMIN_EMPRESA}/${record.company.id}`}
              admin
              withoutColor
            />
          )}
        />

        <AppTable.Column
          width={240}
          title={t('Razón social')}
          render={(_, record) => (
            <LinkTextField
              label={
                record.shipment_payment_company?.full_name
                  ? `${record.shipment_payment_company.full_name}`
                  : `${record.shipment_payment_company.id}`
              }
              to={`${COMPANY_URLS.ADMIN_EMPRESA}/${record.shipment_payment_company.id}`}
              admin
              withoutColor
            />
          )}
        />
        <AppTable.Column
          title={t('Facturas')}
          width={150}
          dataIndex="billing_numbers"
          render={billing_numbers => (
            <>
              {billing_numbers.map(v => (
                <div key={v}>• {v}</div>
              ))}
            </>
          )}
        />
        <AppTable.Column
          width={150}
          dataIndex="created_by"
          title={t('Creado por')}
          render={(_, record) => `${record.created_by.first_name} ${record.created_by.last_name}`}
        />
        <AppTable.Column
          width={150}
          render={(_, record) => (
            <PackageStateTag
              packageStatus={
                record.status == PACKAGE_STATES.GESTION_PAGO
                  ? PACKAGE_STATES.GESTION_PAGO
                  : record.status == PACKAGE_STATES.CANCELADO
                    ? PACKAGE_STATES.CANCELADO
                    : PACKAGE_STATES.PAGADO
              }
            />
          )}
        />
      </AppTable>
    </List>
  );
};

export default SparePartsCollectionList;
